
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import cn from "classnames";

import { FiltersList } from "./FiltersList";
import { ICheck, UpdatePageModel } from "../UpdatePageModel";
import { ConfirmIcon, DeviceCounts, StageStateBar, StageStateColor, StageStateIcon } from "components";
import { StageState, UpdateState } from "enums";
import { useLuxonTimezone } from "hooks";
import { IFilter, IStage } from "interfaces";
import { UsersService } from "services";
import { formatDate, formatTime } from "services/Utils";
import { StageMenu } from "./StageMenu";

interface IStageCardProps {
    canEdit: boolean,
    dragHandleProps: DraggableProvidedDragHandleProps,
    model: UpdatePageModel,
    stage: IStage,
    stageIndex: number,
    onEditFilter: (filter: IFilter, isNew: boolean) => void,
    onEditStage: (stage: IStage, isNew: boolean) => void,
    onDeleteStage: (stage: IStage) => void,
}

export function StageCard({ canEdit, dragHandleProps, model, stage, stageIndex, onEditFilter, onEditStage, onDeleteStage }: IStageCardProps) {
    const { t } = useTranslation();
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);
    const menuRef = useRef<HTMLDivElement>();

    function onClickStage(e: React.MouseEvent) {
        // Ignore clicks from our menu button
        if (menuRef.current && menuRef.current.contains(e.target as Node)) {
            return;
        }
        onEditStage(stage, false);
    }
    
    // See if we have any failing checks for this stage. If so, we'll only display the first.
    let failingCheck: ICheck;
    const stageChecks = model.scheduleChecks[stage.id];
    if (stageChecks && stageChecks.length > 0) {
        failingCheck = stageChecks.find(check => !check.isPassing);
    }

    const canConfirm = model.canConfirmStage(model.update, stage, stageIndex);

    return (<>
        <div
            className="update_card clickable"
            onClick={onClickStage}
            {...dragHandleProps}
        >
            <div className="update_card_title ellipsis">
                {model.update.state >= UpdateState.Running && (
                    <StageStateBar state={stage.state} />
                )}
                <div>
                    <StageStateIcon state={stage.state} />
                    Stage {stageIndex + 1}
                </div>
                <StageStateColor className="update_card_status" state={stage.state}>
                    {t('stageState.' + stage.state)} - {t('stageStateDescription.' + stage.state)}
                </StageStateColor>
            </div>
            {stage.requiresConfirmation && (
                <div className={cn('update_card_confirmMessage', {
                    waitingOnConfirm: canConfirm,
                })}>
                    <ConfirmIcon isConfirmed={stage.isConfirmed} className="update_card_confirmMessageIcon" />
                    {!stage.isConfirmed && `Requires confirmation before starting`}
                    {stage.isConfirmed && `Stage has been confirmed.`}
                </div>
            )}
            <div className="update_card_schedule">
                <div className="update_card_scheduleTitle">
                    Deploy window:
                </div>
                <div className="update_card_scheduleBody">
                    <div>
                        <span className="update_card_scheduleItemLabel">Start:</span>
                        <span className="update_card_scheduleItemValue">
                            {stage.startTime && <>
                                <b>{formatDate(stage.startTime, timezone)}</b> at <b>{formatTime(stage.startTime, timezone, hasTimezone)}</b>
                            </>}
                            {!stage.startTime && <>
                                <b>{`<No start date set>`}</b>
                            </>}
                        </span>
                    </div>
                    <div>
                        <span className="update_card_scheduleItemLabel">End:</span>
                        <span className="update_card_scheduleItemValue">
                            {stage.stopTime && <>
                                <b>{formatDate(stage.stopTime, timezone)}</b> at <b>{formatTime(stage.stopTime, timezone, hasTimezone)}</b>
                            </>}
                            {!stage.stopTime && <>
                                <b>{`<No end date set>`}</b>
                            </>}
                        </span>
                    </div>
                </div>
            </div>
            {stage.notes && (
                <div className="update_card_description">
                    {stage.notes}
                </div>
            )}
            <div className="update_card_targets">
                <div>
                    <DeviceCounts
                        animateInProgress={stage.state === StageState.Running}
                        counts={model.update.counts.stages?.[stageIndex]}
                        hasStarted={stage.state >= StageState.Running}
                    />
                </div>
            </div>
            {failingCheck && (
                <div className={cn("update_card_warnings", {
                    error: failingCheck.isRequired,
                })}>
                    <div className="update_card_warningsIconWrap">
                        {!failingCheck.isRequired && <FontAwesomeIcon icon="exclamation-triangle" />}
                        {failingCheck.isRequired && <FontAwesomeIcon icon="times" />}
                    </div>
                    <div className="update_card_warningsText">
                        {failingCheck.message}
                    </div>
                </div>
            )}
            <StageMenu 
                canEdit={canEdit} 
                className="update_cardMenu"
                isNewStage={false}
                menuRef={menuRef}
                model={model}
                stage={stage}
                onDelete={() => onDeleteStage(stage)}
            />
        </div>
        <FiltersList
            canEdit={canEdit}
            isGlobal={false}
            filters={stage.filters}
            model={model}
            onEditFilter={onEditFilter}
            stageId={stage.id}
            stageIndex={stageIndex}
        />
    </>);
}